import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotification, useTranslate } from "@refinedev/core";

import { ApiError } from "@shared/types";
import { Agreement } from "@modules/agreements/types/agreement";
import { AgreementFormValues } from "@modules/agreements/validations/agreementSchema";
import { Optional } from "@shared/types/optional";
import { editAgreementService } from "./service";
import { UseEditAgreementMutationOptions } from "./types";
import { listAgreementsQueryKey } from "../../queries/listAgreements/query";
import requestErrorTreatment from "@shared/utils/requestErrorTreatment";

export function useEditAgreementMutation(
  id: string,
  options?: UseEditAgreementMutationOptions
) {
  const { open } = useNotification();
  const translate = useTranslate();
  const queryClient = useQueryClient();

  return useMutation<
    Agreement,
    ApiError,
    Optional<AgreementFormValues, "name">
  >({
    mutationKey: ["agreement", id],
    mutationFn: (data: Optional<AgreementFormValues, "name">) =>
      editAgreementService(id as string, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listAgreementsQueryKey,
        refetchType: "all",
      });

      open?.({
        type: "success",
        message: translate("v1/agreement_admin.success.edit"),
      });
    },
    onError: (error: ApiError) => {
      requestErrorTreatment(error, open);
    },
    ...options,
  });
}
